import React from "react";
import i18next, * as I18nTypes from "i18next";
import { TransProps, Trans } from "react-i18next";
import { I18nContext } from "./I18nContext"
import { ReactI18nSSR } from "./I18nSSR";

export { ReactI18nSSR, I18nContext };

type TranslationNode = {
	[k: string]: string | TranslationNode
}

type TranslationData = {
	[l: string]: TranslationNode
}

export class I18nStorage<T extends string> {
	i18n: I18nTypes.i18n;
	parentI18nStorages: I18nStorage<T>[];
	TKeys: T;
	loadingPromise: Promise<any>;

	constructor(
		data: TranslationData,
		i18nOptions: I18nTypes.InitOptions = null,
		parentI18nStorages: I18nStorage<T>[] = [],
	) {
		this.i18n = i18next.createInstance();
		const i18nLoadPromise = this.i18n.init({
			resources: {
				en: { translation: data.en },
				ru: { translation: data.ru },
			},
			keySeparator: ".",
			lng: i18next.language,
			...i18nOptions,
		});
		i18next.on("languageChanged", (lng) => {
			this.setLanguage(lng);
		});
		this.parentI18nStorages = parentI18nStorages;
		this.loadingPromise = Promise.all([
			i18nLoadPromise,
			...parentI18nStorages.map(storage => storage.loadingPromise)
		]);
	}

	t(msgPath: T, options: I18nTypes.TOptions | string = {}): string {
		if (this.i18n.exists(msgPath, options as I18nTypes.TOptions)) {
			return this.i18n.t(msgPath, options);
		}
		for (let i18nStorage of this.parentI18nStorages) {
			const msgRes = i18nStorage.t(msgPath, options);
			if (msgRes) {
				return msgRes;
			}
		}
		console.warn(`Msg "${msgPath}" not found`);
		return "";
	}

	getFixedT(lng: string) {
		return (msgPath: T, options: I18nTypes.TOptions | string = {}) => {
			if (this.i18n.exists(msgPath, options as I18nTypes.TOptions)) {
				return this.i18n.getFixedT(lng)(msgPath, options);
			}
			for (let i18nStorage of this.parentI18nStorages) {
				const msgRes = i18nStorage.getFixedT(lng)(msgPath, options);
				if (msgRes) {
					return msgRes;
				}
			}
		};
	}

	get language() {
		return this.i18n.language;
	}

	setLanguage(lng: string) {
		return Promise.all([
			this.i18n.changeLanguage(lng),
			...this.parentI18nStorages.map(s => s.setLanguage(lng))
		]);
	}
}

function createUseI18n<T extends string>(i18nStorage: I18nStorage<T>) {
	return function useI18n() {
		const ctx = React.useContext(I18nContext);
		const [language, setLanguage] = React.useState(ctx.lang ?? i18next.language);
		const handleLanguageChange = React.useRef(async (lang: string) => {
			await i18nStorage.setLanguage(lang);
			setLanguage(lang);
		});
		React.useEffect(() => {
			const setDefaultLang = () => handleLanguageChange.current(i18next.language);
			i18nStorage.loadingPromise.then(setDefaultLang);
			i18next.on("initialized", setDefaultLang);
			i18next.on("languageChanged", setDefaultLang);
			return () => {
				i18next.off("initialized", setDefaultLang);
				i18next.off("languageChanged", setDefaultLang);
			};
		}, []);
		const tFunction = ctx.ssr
			? i18nStorage.getFixedT(ctx.lang) as I18nStorage<T>["t"]
			: i18nStorage.t.bind(i18nStorage) as I18nStorage<T>["t"];
		const TransWrapper = (props: Omit<TransProps<any>, "i18nKey"> & { i18nKey: T }) => (
			<Trans
				{...props}
				t={tFunction}
			>
				{props.children}
			</Trans>
		);
		//TransWrapper.name = "ReactI18nTrans";
		return {
			i18nStorage, language,
			t: tFunction,
			lang: ctx?.lang ?? language,
			Trans: TransWrapper
		};
	};
}

// type Primitive = null | undefined | string | number | boolean | symbol | bigint;

// type IsTuple<T extends ReadonlyArray<any>> = number extends T['length'] ? false : true;

// type TupleKeys<T extends ReadonlyArray<any>> = Exclude<keyof T, keyof any[]>;

// type PathImpl<K extends string | number, V> = V extends Primitive ? `${K}` : never | `${K}.${Path<V>}`;

// type ArrayKey = number;

// type Path<T> = T extends ReadonlyArray<infer V> ? IsTuple<T> extends true ? {
//     [K in TupleKeys<T>]-?: PathImpl<K & string, T[K]>;
// }[TupleKeys<T>] : PathImpl<ArrayKey, V> : {
//     [K in keyof T]-?: PathImpl<K & string, T[K]>;
// }[keyof T];

// type FieldValues = Record<string, any>;

// type FieldPath<TFieldValues extends FieldValues> = Path<TFieldValues>;

// FieldPath<T>

export function createI18n<T extends string = string>(
	data: TranslationData,
	i18nOptions: I18nTypes.InitOptions = {},
	parentI18nStorages: I18nStorage<T>[] = []
)  {
	const storage = new I18nStorage(data, i18nOptions, parentI18nStorages);
	return { storage, useI18n: createUseI18n<T>(storage), t: storage.t.bind(storage) };
}
